import { Component, Input, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { ButtonModule } from 'primeng/button';
import { FormsModule } from '@angular/forms';
import { ViewChild } from '@angular/core';
import { Table } from 'primeng/table';
import { ProgressSpinnerModule } from 'primeng/progressspinner'; // For p-progressSpinner if you add it
import { TooltipModule } from 'primeng/tooltip';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { Subject, takeUntil } from 'rxjs';

import { MessageService, ConfirmationService } from 'primeng/api'; // Import services for messages and confirmations
import { TranslateService } from '../../../../services/translate.service';
import { MaintainAccountService } from '../maintain-account.service';

import { Url } from '../../../../models/user.model';
import { UrlsComponent } from '../../create-account/steps/urls/urls.component';

import { Pagination } from '../../../../services/pagination.abstruct';

@Component({
	selector: 'app-tab-urls',
	standalone: true,
	imports: [
		CommonModule,
		TableModule,
		TagModule,
		TooltipModule,
		ButtonModule,
		ProgressSpinnerModule,
		FormsModule
	],
	templateUrl: './tab-urls.component.html',
	styleUrls: ['./tab-urls.component.scss'],
	providers: [DialogService, MessageService]
})
export class TabUrlsComponent extends Pagination implements OnInit, OnDestroy {
	@Input() urls!: any[];
	@Input() urlsLoading!: boolean;
	@Input() urlsError!: boolean;
	@Input() customerRecId!: number;


	@ViewChild('dt') dt!: Table;

	ref: DynamicDialogRef | undefined;
	private destroy$ = new Subject<void>();

	globalFilter: string = '';
	globalFilterFields: string[] = [];

	constructor(public translateServ: TranslateService,
		private messageService: MessageService,
		private confirmationService: ConfirmationService,
		private maintainAccountService: MaintainAccountService,
		private dialogService: DialogService) {super(); }

	ngOnInit(): void {
		if (this.urls?.length > 0) {
			this.globalFilterFields = Object.keys(this.urls[0]);
		}
	}

	ngOnDestroy(): void {
		if (this.ref) {
			this.ref.close();
		}
	}

	getUrlSeverity(isActive: number): string {
		return isActive === 1 ? 'success' : 'danger';
	}

	editUrl(url: Url): void {
		this.ref = this.dialogService.open(UrlsComponent, {
			header: this.translateServ.dictionary['STEPPER_DIALOG_TITLE_EDIT'] || 'Edit Record',
			width: '70%',
			contentStyle: { overflow: 'auto' },
			baseZIndex: 10000,
			maximizable: true,
			data: {
				isDialog: true,
				isCreate: false,
				customerRecId: this.customerRecId,
				nameEntry: {
					id: url.RecID.toString(),
					recId: url.UrlRecID,
					url: url.Url,
					UrlRecID: url.UrlRecID,
					description: url.Descr,
					isActive: url.IsActive,
					isApproved: url.IsApproved,
					// customerRecId: url.RecID
				}
			}
		});

		this.ref.onClose.pipe(takeUntil(this.destroy$)).subscribe((result) => {
			if (result) {
				this.messageService.add({
					severity: 'success',
					summary: this.translateServ.dictionary['SUCCESS'] || 'Success',
					detail: this.translateServ.dictionary['MAINT_UPDATE_SUCCESS_MSG'] || 'Record updated successfully.',
					life: 3000
				});
				this.reloadAUrlsForUser();
			}
		});
	}

	/**
	 * Function to update the status of an additional name via an API call.
	 * It takes the RecID and the status as a number (1 for active, 0 for inactive).
	 * @param recId The RecID of the additional name to update.
	 * @param newStatus The new status (1 for active, 0 for inactive).
	 */
	updateURLStatus(source: boolean, recId: number, newStatus: 0 | 1): void {
		// Calling the API service to update the status
		this.maintainAccountService.updateFunctions(source, this.constructor.name.substring(1), recId, newStatus).subscribe({
			next: (response: any) => {
				// Check if the API response indicates a successful update
				if (response && response[0]?.RecID && response[0].RecID !== -1) {
					const index = this.urls?.findIndex(name => name.RecID === recId);
					if (this.urls && index !== undefined && index > -1) {
						// Correctly updating the 'IsActive' or 'IsApproved' property
						if (source) {
							this.urls[index].IsActive = newStatus;
						}
						else {
							this.urls[index].IsApproved = newStatus;
						}
						const statusText = newStatus === 1 ? 'Active' : 'Inactive';
						this.messageService.add({
							severity: 'success',
							summary: this.translateServ.dictionary['STATUS_UPDATED'] || 'Status Updated',
							// Correctly using the 'Street' property for the message
							detail: `URL: "${this.urls[index].Url}" is now ${statusText}.`,
							life: 3000
						});
					}
				} else {
					this.messageService.add({
						severity: 'error',
						summary: this.translateServ.dictionary['ERROR'] || 'Error',
						detail: this.translateServ.dictionary['STATUS_UPDATE_FAILED'] || 'Failed to update status. Record not found or invalid response.',
						life: 5000
					});
				}
			},
			error: (err: any) => {
				console.error('API Error:', err);
				this.messageService.add({
					severity: 'error',
					summary: this.translateServ.dictionary['ERROR'] || 'Error',
					detail: this.translateServ.dictionary['STATUS_UPDATE_FAILED'] || 'Failed to update status. Please try again.',
					life: 5000
				});
			}
		});
	}


	toggleUrlStatus(url: Url, action: boolean): void {
		const newStatus = action ? (url.IsActive === 1 ? 0 : 1) : (url.IsApproved === 1 ? 0 : 1);
		const actionText = newStatus === 1 ?
			(this.translateServ.dictionary['ACTIVATE'] || 'activate') :
			(this.translateServ.dictionary['DEACTIVATE'] || 'deactivate');

		this.confirmationService.confirm({
			message: `${this.translateServ.dictionary['CONFIRM_CHANGE_STATUS'] || 'Are you sure you want to'} ${actionText} ${url.Url}?`,
			header: this.translateServ.dictionary['CONFIRM_ACTION'] || 'Confirm Action',
			icon: 'pi pi-exclamation-triangle',
			acceptLabel: this.translateServ.dictionary['YES'] || 'Yes',
			rejectLabel: this.translateServ.dictionary['NO'] || 'No',
			accept: () => {
				this.updateURLStatus(action, url.RecID, newStatus);
			},
			reject: () => {
				this.messageService.add({
					severity: 'info',
					summary: this.translateServ.dictionary['CANCELLED'] || 'Cancelled',
					detail: this.translateServ.dictionary['ACTION_CANCELLED'] || 'Action cancelled.',
					life: 3000
				});
			}
		});
	}

	createNewRecord(): void {
		// FIX: Pass the customerRecId to the AdditionalNamesComponent via the data property
		this.ref = this.dialogService.open(UrlsComponent, {
			header: this.translateServ.dictionary['STEPPER_DIALOG_TITLE'] || 'Create New Record',
			width: '70%',
			contentStyle: { overflow: 'auto' },
			baseZIndex: 10000,
			maximizable: true,
			data: {
				isDialog: true,
				isCreate: true,
				customerRecId: this.customerRecId // Pass the ID here
			}
		});

		// Subscribe to the dialog's onClose event
		this.ref.onClose.pipe(takeUntil(this.destroy$)).subscribe((result) => {
			// Check if the dialog was closed with a successful action (e.g., a "save" button click)
			// The AdditionalNamesComponent would need to return a value like 'true' upon success
			// to trigger this.
			if (result) {
				this.messageService.add({
					severity: 'success',
					summary: this.translateServ.dictionary['SUCCESS'] || 'Success',
					detail: this.translateServ.dictionary['MAINT_CREATE_SUCCESS_MSG'] || 'Record created successfully.',
					life: 3000
				});
				this.reloadAUrlsForUser();
			}
		});
	}

	// Confirms and deletes a record
	confirmDelete(url: Url): void {
		this.confirmationService.confirm({
			message: `${this.translateServ.dictionary['MAINT_DELETE_CONFIRM_MSG'] || 'Are you sure you want to delete this record?'} (ID: ${url.RecID})`,
			header: this.translateServ.dictionary['MAINT_DELETE_CONFIRM_HEADER'] || 'Confirm Deletion',
			icon: 'pi pi-exclamation-triangle',
			acceptLabel: this.translateServ.dictionary['CONFIRM_DELETE'] || 'Delete',
			rejectLabel: this.translateServ.dictionary['CONFIRM_CANCEL'] || 'Cancel',
			accept: () => {
				this.maintainAccountService.deleteRecord(this.constructor.name.substring(1), url.RecID)
					.pipe(takeUntil(this.destroy$))
					.subscribe({
						next: (response) => {
							this.messageService.add({
								severity: 'success',
								summary: this.translateServ.dictionary['SUCCESS'] || 'Success',
								detail: this.translateServ.dictionary['MAINT_DELETE_SUCCESS_MSG'] || 'Record deleted successfully.',
								life: 3000,
							});
							this.reloadAUrlsForUser();
						},
						error: (error) => {
							console.error('Failed to delete record:', error);
							this.messageService.add({
								severity: 'error',
								summary: this.translateServ.dictionary['ERROR'] || 'Error',
								detail: this.translateServ.dictionary['MAINT_DELETE_FAILED_MSG'] || 'Failed to delete record.',
								life: 5000,
							});
						}
					});
			},
			reject: () => {
				// The user clicked "Cancel", so we can just close the dialog.
			}
		});
	}

	// Reloads the data for the additional names table
	private reloadAUrlsForUser(): void {
		// Call the service method to get the updated data
		const index =  this.maintainAccountService.getMappingIndex("urls");
		const instance = this.maintainAccountService.getUserDataMappings();
		this.maintainAccountService.getDatas(this.customerRecId, instance[index].apiName, instance[index].special)
			.pipe(takeUntil(this.destroy$))
			.subscribe({
				next: (data) => {
					this.urls = data;
					this.urlsLoading = false;
				},
				error: (error) => {
					console.error('Failed to reload urls:', error);
					this.urlsError = true;
					this.urlsLoading = false;
					this.messageService.add({
						severity: 'error',
						summary: this.translateServ.dictionary['ERROR'] || 'Error',
						detail: this.translateServ.dictionary['MAINT_LOAD_FAILED_MSG_URL'] || 'Failed to load Urls.',
						life: 5000
					});
				}
			});
	}

	applyGlobalFilter(): void {
		this.dt.filterGlobal(this.globalFilter, 'contains');
	}
}